import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import {
  Iso900114001Input,
  Iso900114001Textarea,
} from "../../../standards/iso-9001-14001/components/shared/Iso900114001Fields";
import { Iso900114001Modal } from "../../../standards/iso-9001-14001/components/shared/Iso900114001Modal";
import {
  Iso2700127701Button,
  Iso2700127701InputField,
  Iso2700127701Modal,
  Iso2700127701TextareaField,
} from "../../../standards/iso-27001-27701/components/shared";
import type {
  Indicador,
  IndicadoresStandard,
  IndicadorMetadataPayload,
} from "../types/indicadores";

type Props = {
  standard: IndicadoresStandard;
  open: boolean;
  indicator: Indicador | null;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: IndicadorMetadataPayload) => void;
};

export function IndicadorMetadataModal({
  standard,
  open,
  indicator,
  submitting,
  error,
  onClose,
  onSubmit,
}: Props) {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [criterio1, setCriterio1] = useState("");
  const [criterio2, setCriterio2] = useState("");

  useEffect(() => {
    if (!open || !indicator) return;

    setNombre(indicator.nombre);
    setDescripcion(indicator.descripcion ?? "");
    setCriterio1(indicator.criterio_1 ?? "");
    setCriterio2(indicator.criterio_2 ?? "");
  }, [indicator, open]);

  if (!indicator) return null;

  const submit = () => {
    onSubmit({
      nombre: nombre.trim(),
      descripcion: nullable(descripcion),
      criterio_1: nullable(criterio1),
      criterio_2: nullable(criterio2),
    });
  };

  if (standard === "iso-9001-14001") {
    return (
      <Iso900114001Modal
        open={open}
        title="Editar información general"
        eyebrow="Configuración del indicador"
        submitting={submitting}
        error={error}
        onClose={onClose}
        onSubmit={submit}
        wide
        footer={
          <>
            <button
              type="button"
              className="calidad-back-button"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="calidad-primary-button"
              disabled={submitting || nombre.trim() === ""}
            >
              <Save size={16} />
              {submitting ? "Guardando..." : "Guardar"}
            </button>
          </>
        }
      >
        <Iso900114001Input
          label="Nombre del indicador"
          type="text"
          value={nombre}
          required
          maxLength={255}
          fullWidth
          onChange={(event) => setNombre(event.target.value)}
        />
        <Iso900114001Textarea
          label="Descripción"
          value={descripcion}
          rows={7}
          fullWidth
          onChange={(event) => setDescripcion(event.target.value)}
        />
        <Iso900114001Textarea
          label="Criterio 1"
          value={criterio1}
          rows={7}
          onChange={(event) => setCriterio1(event.target.value)}
        />
        <Iso900114001Textarea
          label="Criterio 2"
          value={criterio2}
          rows={7}
          onChange={(event) => setCriterio2(event.target.value)}
        />
      </Iso900114001Modal>
    );
  }

  return (
    <Iso2700127701Modal
      open={open}
      title="Editar información general"
      description="Actualiza el nombre, la descripción y los dos criterios del indicador."
      size="wide"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Iso2700127701Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </Iso2700127701Button>
          <Iso2700127701Button
            variant="primary"
            type="submit"
            icon={<Save size={16} />}
            disabled={submitting || nombre.trim() === ""}
          >
            {submitting ? "Guardando..." : "Guardar"}
          </Iso2700127701Button>
        </>
      }
    >
      <div className="iso27001-form-grid">
        <Iso2700127701InputField
          label="Nombre del indicador"
          value={nombre}
          required
          maxLength={255}
          fullWidth
          onChange={(event) => setNombre(event.target.value)}
        />
        <Iso2700127701TextareaField
          label="Descripción"
          value={descripcion}
          rows={7}
          fullWidth
          onChange={(event) => setDescripcion(event.target.value)}
        />
        <Iso2700127701TextareaField
          label="Criterio 1"
          value={criterio1}
          rows={7}
          onChange={(event) => setCriterio1(event.target.value)}
        />
        <Iso2700127701TextareaField
          label="Criterio 2"
          value={criterio2}
          rows={7}
          onChange={(event) => setCriterio2(event.target.value)}
        />
      </div>
      {error ? <div className="iso27001-form-error">{error}</div> : null}
    </Iso2700127701Modal>
  );
}

function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}
